import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';

// API/Components
import {
	ANALYSIS_RESULT,
	ANALYSIS_TYPE,
	DATA_ERROR_VALUE,
	GRAPH_BOUNDS,
	isEmpty,
	validValue,
	AnalysisResultToStateColor,
	measUnitsConversion,
	measUnitsArrayConversion,
	getUserTempUnits
} from './enums';
import { DrawVerticalDashedLineAtTooltip } from './chart_helpers';
import {
	createUnitGraphPopover,
} from './unit_graph_helpers';

// Material UI
import Button from '@mui/material/Button';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import Grid from '@mui/material/Grid';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	BarController,
	ScatterController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import tinycolor from "tinycolor2";

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	BarController,
	ScatterController,
	zoomPlugin,
);


export default function UnitSnapshotGraph(props) {
	const { snapshotData, limitData, graphScalesExtremes, selectedString, numUnits, graphType } = props;
	const { locationNum } = useParams();
	
	const [graphData, setGraphData] = useState([]);
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
		maxminEnabled: true,
		avgEnabled: false,
		ambientEnabled: true,
		initialsEnabled: false,
	});
	const [graphOptions, setGraphOptions] = useState({
		maintainAspectRatio: false,
		animation: false,
		options: {
			responsive: true,
		},
		transitions: {
			zoom: {
				animation: {
					duration: 0,
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				intersect: false,
				mode: 'index',
				...createUnitGraphPopover(),
			},
			zoom: {
				pan: {
					enabled: true,
					mode: 'xy',
				},
				limits: {
					x: {
						minRange: 2,
					},
					y: {
						min: 0,
						max: 100,
						minRange: 5,
					}
				},
				zoom: {
					mode: 'y',
					wheel: {
						enabled: true,
						speed: 0.02,
					},
					pinch: {
						enabled: true,
					},
					onZoomStart: (e) => {
						if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
							return;
						}
						if (e.event.shiftKey === true) {
							e.chart.config.options.plugins.zoom.zoom.mode = "x";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
						} else {
							e.chart.config.options.plugins.zoom.zoom.mode = "y";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
						}
					},
				}
			},
		},
	});
	
	// const [isLoading, setIsLoading] = useState(loading);
	
	const chartRef = useRef(null);
	const graphTypeRef = useRef(graphType);
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	const graphPlugins = [{
		afterDraw: chart => DrawVerticalDashedLineAtTooltip(chart),
	}];

	useEffect(() => {
		if (typeof snapshotData != "undefined" && snapshotData !== null && !isEmpty(snapshotData)) {

			const unitsData = GetGraphData(snapshotData, selectedString, numUnits, graphType);
			if (unitsData !== undefined && unitsData !== null) {
				setGraphData(unitsData);
			}
		}
	}, [numUnits, snapshotData, selectedString, graphType, graphSettings, limitData]);

	useEffect(() => {
		const graphScales = SetGraphYScale(graphType);
		if (graphScales !== undefined && graphScales !== null) {
			setGraphOptions((prevState) => {
				return {
					...prevState,
					scales: {
						y: graphScales,
						x: {
							title: {
								display: true,
								text: "Units",
								font: {
									family: "Saira, sans-serif",
									weight: 600,
									size: 18,
								},
							}
						}
					}
				}
			});
		}
	}, [graphScalesExtremes, graphType, selectedString]);
	
	useEffect(() => {
		SetGraphBounds();
	}, [graphType]);

	function SetGraphBounds() {
		let graphZoom = GRAPH_BOUNDS.UNIT_VOLTAGE;
		switch(graphType) {
			case 0:  // Voltage
				graphZoom = GRAPH_BOUNDS.UNIT_VOLTAGE;
				break;

			case 1:  // Impedance
				graphZoom = GRAPH_BOUNDS.UNIT_IMPEDANCE;
				break;

			case 2:  // Temperature
				graphZoom = {
					...GRAPH_BOUNDS.TEMPERATURE,
					bounds_max: measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_max),
					bounds_min: measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_min),
				};
				break;

			case 3:  // Differential Impedance
				graphZoom = GRAPH_BOUNDS.DIFF_IMPEDANCE;
				break;

			default:
				graphZoom = GRAPH_BOUNDS.UNIT_VOLTAGE;
				break;
		}
		
		setGraphOptions((prevState) => {
			return {
				...prevState,
				plugins: {
					...prevState.plugins,
					zoom: {
						...prevState.plugins.zoom,
						limits: {
							...prevState.plugins.zoom.limits,
							y: {
								max: graphZoom.bounds_max,
								min: graphZoom.bounds_min,
								minRange: graphZoom.min_range
							},
						},
					},
				},
			}
		});
	}

	function GetMaxAvgMinData(stringStats, selectedString, numUnits, graphType) {
		let maxValues = [];
		let avgValues = [];
		let minValues = [];
		
		if (stringStats !== undefined && stringStats !== null) {
			stringStats?.forEach(string => {
				if (selectedString === 0 || string.string_nbr === selectedString) {
					let max = 0;
					let avg = 0;
					let min = 0;

					switch(graphType) {
						case 0:
							max = string.stats.volt.max;
							avg = string.stats.volt.avg;
							min = string.stats.volt.min;
							break;
						case 1:
							max = string.stats.imped.max;
							avg = string.stats.imped.avg;
							min = string.stats.imped.min;
							break;
						case 2:
							max = measUnitsConversion(string.stats.temp.max);
							avg = measUnitsConversion(string.stats.temp.avg);
							min = measUnitsConversion(string.stats.temp.min);
							break;
						case 3:
							max = string.stats.diff_imp.max;
							avg = string.stats.diff_imp.avg;
							min = string.stats.diff_imp.min;
							break;
						default:
							max = string.stats.volt.max;
							avg = string.stats.volt.avg;
							min = string.stats.volt.min;
							break;
					}

					// Push the max/avg/min values to the appropriate arrays
					//  for the number of units in the string
					for (let i = 0; i < numUnits; i++) {
						maxValues.push(max);
						avgValues.push(avg);
						minValues.push(min);
					}
				}
			});
		}
		
		// Get the associated limit colors
		let maxColor = '#0033FF';
		let avgColor = '#005500';
		let minColor = '#0033FF';

		let statSets = {
			'maxSet': {},
			'avgSet': {},
			'minSet': {}
		};
		if (maxValues !== undefined && maxValues.length > 0) {
			statSets.maxSet = {
				type: 'scatter',
				label: 'String Max',
				backgroundColor: maxColor + "55",
				borderColor: maxColor + "77",
				borderWidth: 1,
				pointStyle: 'triangle',
				radius: 4,
				rotation: 180,
				hitRadius: 5,
				data: maxValues,
				order: 4,
			};
		}
		
		if (avgValues !== undefined && avgValues.length > 0) {
			statSets.avgSet = {
				type: 'scatter',
				label: 'String Avg',
				backgroundColor: avgColor + "55",
				borderColor: avgColor + "77",
				borderWidth: 1,
				pointStyle: 'crossRot',
				radius: 5,
				hitRadius: 5,
				data: avgValues,
				order: 5,
			};
		}

		if (minValues !== undefined && minValues.length > 0) {
			statSets.minSet = {
				type: 'scatter',
				label: 'String Min',
				backgroundColor: minColor + "55",
				borderColor: minColor + "77",
				borderWidth: 1,
				pointStyle: 'triangle',
				radius: 4,
				rotation: 0,
				hitRadius: 5,
				data: minValues,
				order: 6,
			};
		}

		return statSets;
	}

	function GetLimitsData(selectedString, graphType) {
		let critHiLimits = [];
		let maintHiLimits = [];
		let maintLoLimits = [];
		let critLoLimits = [];
		
		if (limitData !== undefined && limitData !== null) {
			limitData?.forEach(string => {
				if (selectedString === 0 || string.string_nbr === selectedString) {
					switch(graphType) {
						case 0:  // Voltage
							Array.prototype.push.apply(critHiLimits, string.volt.hi_crit);
							Array.prototype.push.apply(maintHiLimits, string.volt.hi_maint);
							Array.prototype.push.apply(maintLoLimits, string.volt.lo_maint);
							Array.prototype.push.apply(critLoLimits, string.volt.lo_crit);
							break;
						case 1:  // Impedance
							Array.prototype.push.apply(critHiLimits, string.imped.hi_crit);
							Array.prototype.push.apply(maintHiLimits, string.imped.hi_maint);
							break;
						case 2:  // Temperature
							Array.prototype.push.apply(critHiLimits, measUnitsArrayConversion(string.temp.hi));
							Array.prototype.push.apply(critLoLimits, measUnitsArrayConversion(string.temp.lo));
							break;
						case 3:  // Differential Impedance
							Array.prototype.push.apply(critHiLimits, string.diff_imp.hi_crit);
							Array.prototype.push.apply(maintHiLimits, string.diff_imp.hi_maint);
							break;
						default:  // Voltage
							Array.prototype.push.apply(critHiLimits, string.volt.hi_crit);
							Array.prototype.push.apply(maintHiLimits, string.volt.hi_maint);
							Array.prototype.push.apply(maintLoLimits, string.volt.lo_maint);
							Array.prototype.push.apply(critLoLimits, string.volt.lo_crit);
							break;
					}
				}
			});
		}

		// Get the associated limit colors
		let hiCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL);
		let hiMaintColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS);
		let loMaintColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS);
		let loCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL);

		let limitSets = [];
		if (critHiLimits !== undefined && critHiLimits.length > 0) {
			limitSets.push({
				type: 'scatter',
				label: 'Crit High',
				backgroundColor: hiCritColor + "77",
				borderColor: hiCritColor,
				borderWidth: 2,
				pointStyle: 'rect',
				radius: 4,
				hitRadius: 5,
				data: critHiLimits,
				order: 0,
			});
		}

		if (maintHiLimits !== undefined && maintHiLimits.length > 0) {
			limitSets.push({
				type: 'scatter',
				label: 'Maint High',
				backgroundColor: hiMaintColor + "77",
				borderColor: hiMaintColor,
				borderWidth: 2,
				pointStyle: 'rect',
				radius: 4,
				hitRadius: 5,
				data: maintHiLimits,
				order: 0,
			});
		}

		if (maintLoLimits !== undefined && maintLoLimits.length > 0) {
			limitSets.push({
				type: 'scatter',
				label: 'Maint Low',
				backgroundColor: loMaintColor + "77",
				borderColor: loMaintColor,
				borderWidth: 2,
				pointStyle: 'rect',
				radius: 4,
				hitRadius: 5,
				data: maintLoLimits,
				order: 0,
			});
		}

		if (critLoLimits !== undefined && critLoLimits.length > 0) {
			limitSets.push({
				type: 'scatter',
				label: 'Crit Low',
				backgroundColor: loCritColor + "77",
				borderColor: loCritColor,
				borderWidth: 2,
				pointStyle: 'rect',
				radius: 4,
				hitRadius: 5,
				data: critLoLimits,
				order: 0,
			});
		}
		
		return limitSets;
	}

	function SetGraphYScale(graphType) {
		
		if (graphScalesExtremes !== undefined && graphScalesExtremes !== null && !isEmpty(graphScalesExtremes)) {

			let max = DATA_ERROR_VALUE.INVALID_VALUE;
			let min = DATA_ERROR_VALUE.INVALID_VALUE;
			let title = "";
			
			switch (graphType) {
				case 0:  // Voltage
					max = validValue(graphScalesExtremes.voltage?.max, GRAPH_BOUNDS.UNIT_VOLTAGE.default_max);
					min = validValue(graphScalesExtremes.voltage?.min, GRAPH_BOUNDS.UNIT_VOLTAGE.default_min);
					title = `Unit Voltage (${ANALYSIS_TYPE.VOLTAGE.unit})`;
					break;

				case 1:  // Impedance
					max = validValue(graphScalesExtremes.impedance?.max, GRAPH_BOUNDS.UNIT_IMPEDANCE.default_max);
					min = validValue(graphScalesExtremes.impedance?.min, GRAPH_BOUNDS.UNIT_IMPEDANCE.default_min);
					title = `Unit Impedance (${ANALYSIS_TYPE.IMPEDANCE.unit})`;
					break;

				case 2:  // Temperature
					const maxC = validValue(graphScalesExtremes.temperature?.max, GRAPH_BOUNDS.TEMPERATURE.default_max);
					const minC = validValue(graphScalesExtremes.temperature?.min, GRAPH_BOUNDS.TEMPERATURE.default_min);
					max = measUnitsConversion(maxC);
					min = measUnitsConversion(minC);
					title = `Unit Temperature (${getUserTempUnits()})`;
					break;

				case 3:  // Differential Impedance
					max = GRAPH_BOUNDS.DIFF_IMPEDANCE.default_max;  // no setting for differential impedance
					min = GRAPH_BOUNDS.DIFF_IMPEDANCE.default_min;
					title = `Unit Differential Impedance (${ANALYSIS_TYPE.IMPEDANCE.diff_unit})`;
					break;

				default:  // Voltage
					max = validValue(graphScalesExtremes.voltage?.max, GRAPH_BOUNDS.UNIT_VOLTAGE.default_max);
					min = validValue(graphScalesExtremes.voltage?.min, GRAPH_BOUNDS.UNIT_VOLTAGE.default_min);
					title = `Unit Voltage (${ANALYSIS_TYPE.VOLTAGE.unit})`;
					break;
			}

			if (max !== DATA_ERROR_VALUE.INVALID_VALUE && min !== DATA_ERROR_VALUE.INVALID_VALUE) {
				return {
					max: max,
					min: min,
					title: {
						display: true,
						text: title,
						font: {
							family: "Saira, sans-serif",
							size: 18,
							weight: 600,
						},
					},
				};
			}
		}

		return {};
	}
	
	function GetUnitInfo(unit, graphType) {
		let value, analysis = 0;
		switch(graphType) {
			case 0:
				value = unit.measurement.unit_voltage?.toFixed(3);
				analysis = (unit.analysis.voltage_analysis_valid) ?
					unit.analysis.voltage_analysis_result :	0;
				break;
			case 1:
				value = unit.measurement.unit_imp?.toFixed(3);
				analysis = (unit.analysis.impedance_analysis_valid) ?
					unit.analysis.impedance_analysis_result : 0;
				break;
			case 2:
				let temp = unit.measurement.unit_temp;
				value = measUnitsConversion(temp)?.toFixed(1);
				analysis = (unit.analysis.temperature_analysis_valid) ?
					unit.analysis.temperature_analysis_result : 0;
				break;
			case 3:
				value = unit.analysis.impedance_analysis_out?.toFixed(2);
				analysis = (unit.analysis.impedance_analysis_valid) ?
					unit.analysis.impedance_analysis_result : 0;
				break;
			default:
				value = unit.measurement.unit_voltage?.toFixed(3);
				analysis = (unit.analysis.voltage_analysis_valid) ?
					unit.analysis.voltage_analysis_result : 0;
			break;
		}
		value = (value !== null && value !== undefined && value > DATA_ERROR_VALUE.INVALID_VALUE) ? value : 0;
		let analysisColor = AnalysisResultToStateColor(analysis);

		let color = unit.unit_replaced ? tinycolor(analysisColor).darken(15).toString() : analysisColor;

		return {value, color};
	}

	function GetGraphTypeText(graphType) {
		switch(graphType) {
			case 0:
				return "Unit Voltage";
			case 1:
				return "Unit Impedance";
			case 2:
				return "Unit Temperature";
			case 3:
				return "Unit Differential Impedance";
			default:
				return "Unit Voltage";
		}
	}
	
	
	function GetGraphData(snapshotData, selectedString, numUnits, graphType) {
		// Pick the units to graph based on the selected string
		const unitData = snapshotData?.units?.filter((x) => (selectedString === 0 || x.string_nbr === selectedString));
		if (unitData && unitData.length > 0) {
			
			const filteredData = unitData.map((unit) => {
				const { value, color } = GetUnitInfo(unit, graphType);
				
				return {
					label: (selectedString === 0) ? `S${unit.string_nbr} U${unit.unit_nbr}` : `Unit ${unit.unit_nbr}`,
					value: value,
					barColor: color + "77",  // Make the bar color partially transparent
					borderColor: color,  // The outline should be fully opaque
				};
			});

			const graphTypeText = GetGraphTypeText(graphType);
			
			// Add measurement data to datasets
			let dataSets = []
			dataSets.push({
				type: 'bar',
				label: graphTypeText,
				backgroundColor: filteredData.map((unit) => unit.barColor),
				borderColor: filteredData.map((unit) => unit.borderColor),
				borderWidth: 2,
				data: filteredData.map((unit) => unit.value),
				order: 10,
			});

			// check for limits enabled and add limitsets to datasets if enabled
			if (graphSettings.limitsEnabled) {
				const limitsData = GetLimitsData(selectedString, graphType);
				if (limitsData !== undefined && limitsData.length > 0) {
					limitsData.forEach((limitSet) => {
						dataSets.push(limitSet);
					});
				}
			}
			
			// check for limits enabled and add limitsets to datasets if enabled
			const stringStats = snapshotData?.strings;
			if (graphSettings.maxminEnabled || graphSettings.avgEnabled) {
				const maxavgminData = GetMaxAvgMinData(stringStats, selectedString, numUnits, graphType);
				if (maxavgminData !== undefined) {
					if (graphSettings.maxminEnabled) {
						if (maxavgminData.maxSet !== undefined) { dataSets.push(maxavgminData.maxSet); }
						if (maxavgminData.minSet !== undefined) { dataSets.push(maxavgminData.minSet); }
					}

					if (graphSettings.avgEnabled && maxavgminData.minSet !== undefined && maxavgminData.minSet != {}) {
						dataSets.push(maxavgminData.avgSet);
					}
				}
			}

			// If ambient temperature is enabled and this is the temperature graph,
			// draw a horizontal ambient line across all units in the snapshot.
			if (graphType === 2 && graphSettings.ambientEnabled) {
				const ambientTemp = snapshotData?.system?.measurements?.ambient_temp;
				if (ambientTemp !== undefined && ambientTemp !== null && ambientTemp > DATA_ERROR_VALUE.INVALID_VALUE) {
					const ambientValue = measUnitsConversion(ambientTemp);
					const ambientData = filteredData.map(() => ambientValue);

					dataSets.push({
						type: 'line',
						label: 'Ambient Temperature',
						data: ambientData,
						borderColor: 'rgb(35, 141, 193)',
						backgroundColor: 'rgba(35, 141, 193, 0.7)',
						pointRadius: 0,
						hitRadius: 4,
						borderWidth: 2,
						fill: false,
						spanGaps: true,
						order: 11,
					});
				}
			}

			return {
				labels: filteredData.map((unit) => unit.label),
				datasets: dataSets,
			};
		}

		return null;
	}

	const handleLimitMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			limitsEnabled: !graphSettings.limitsEnabled})
		);
	};

	const handleMaxMinMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			maxminEnabled: !graphSettings.maxminEnabled})
		);
	};

	const handleAvgMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			avgEnabled: !graphSettings.avgEnabled})
		);
	};

	const handleAmbientMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			ambientEnabled: !graphSettings.ambientEnabled})
		);
	};

	// const handleInitialMarkersToggle = () => {
	// 	setGraphSettings((prevState) => ({
	// 		...prevState,
	// 		initialsEnabled: !graphSettings.initialsEnabled})
	// 	);
	// };

	const handleGraphResetZoom = () => {
		chartRef.current.resetZoom();
	};

	return (
		<div
			style={{
				width: '100%',
				height: '99%',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
			}}
		>
			<div style={{ height: '100%', width: '100%' }}>
				{ (graphData !== undefined && graphData.length !== 0) &&
					<Chart
						ref={chartRef}
						type='bar'
						options={graphOptions}
						data={graphData}
						plugins = {graphPlugins}
					/>
				}
			</div>
			<Grid container spacing={1}>
				<Grid item xs alignItems="left">
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value="limitMarkers"
							selected={graphSettings.limitsEnabled}
							onClick={handleLimitMarkersToggle}
						>
							Limits
						</ToggleButton>
						<ToggleButton
							value="maxminMarkers"
							selected={graphSettings.maxminEnabled}
							onClick={handleMaxMinMarkersToggle}
						>
							Max/Min
						</ToggleButton>
						<ToggleButton
							value="avgMarkers"
							selected={graphSettings.avgEnabled}
							onClick={handleAvgMarkersToggle}
						>
							Avg
						</ToggleButton>
						{ graphType === 2 &&
							<ToggleButton
								value="ambientMarkers"
								selected={graphSettings.ambientEnabled}
								onClick={handleAmbientMarkersToggle}
							>
								Ambient
							</ToggleButton>
						}
						{/* { graphType == 1 &&
							<ToggleButton
								value="initialMarkers"
								selected={graphSettings.initialsEnabled && graphType == 1}
								onClick={handleInitialMarkersToggle}
								disabled={graphType != 1}
							>
								Initials
							</ToggleButton>
						} */}
					</ToggleButtonGroup>
				</Grid>
				<Grid item alignItems="right">
					<Button
						variant="contained"
						startIcon={<CenterFocusStrongIcon />}
						onClick={handleGraphResetZoom}
						sx={{
							marginY: 1,
							backgroundColor: 'white',
							border: '1px solid lightgray',
							color: 'black',
							":hover": {
								backgroundColor: 'lightgray',
								boxShadow: 'none',
							}

						}}
					>
						Reset View
					</Button>
				</Grid>
			</Grid>
		</div>
	);
}